import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Sparkles, ArrowRight, BookOpen } from 'lucide-react';
import { aboutContent, siteConfig } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';

export default function OmMig() {
  const { title, text1, text2, values } = aboutContent;

  return (
    <div className="bg-slate-50 pt-16 sm:pt-24 pb-0">
      <SEO 
        title="Om Steffen | Din Certificerede Mindset Coach | SECoaching"
        description="Mød Steffen, din mindsetcoach hos SECoaching. Læs om min passion for at hjælpe dig med mental balance, sunde vaner og personlig udvikling. Læs mere!"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main Bio Section */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-center mb-20">
          {/* Image Column */}
          <div className="lg:col-span-6">
            <ScrollReveal direction="up" duration={900}>
              <div className="relative overflow-hidden rounded-none">
                <img
                  src={siteConfig.images.portrait}
                  alt="Steffen - Mindsetcoach hos SECoaching"
                  className="h-auto max-h-[600px] w-full object-contain bg-slate-100/50 shadow-md"
                />
              </div>
            </ScrollReveal>
          </div>

          {/* Text Column */}
          <div className="lg:col-span-6">
            <ScrollReveal direction="up" duration={800} className="space-y-6">
              <h1 className="text-4xl font-extrabold tracking-tight text-blue-950 sm:text-5xl">
                {title}
              </h1>

              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p className="font-medium text-blue-950">
                  {text1}
                </p>
                <p>
                  {text2}
                </p>
              </div>

              {/* Education / Future Vision Box */}
              <div className="flex gap-4 rounded-none border border-slate-200 bg-white p-5 mt-6 shadow-sm">
                <div className="shrink-0">
                  <BookOpen className="h-8 w-8 text-slate-900" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-blue-950">Fremtidig helhedsorienteret tilgang</h4>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    Ved at kombinere mental coaching med kostvejledning og fysisk træning skaber jeg på sigt et komplet fundament for din personlige transformation.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="border-t border-slate-200/60 pt-20">
          <ScrollReveal direction="up" duration={800} className="space-y-16">
            <div className="text-center max-w-3xl mx-auto space-y-4">
              <h2 className="text-3xl font-bold tracking-tight text-blue-950 sm:text-4xl">
                Mine kerneværdier som coach
              </h2>
              <p className="text-lg text-slate-600">
                Disse værdier danner grundlaget for alle mine samtaler og forløb, så du altid får den bedste oplevelse.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {values.map((value, idx) => (
                <div
                  key={idx}
                  className={`p-4 text-center space-y-4 ${
                    idx > 0
                      ? 'border-t border-slate-200 pt-8 md:border-t-0 md:pt-4 md:border-l md:border-slate-200 md:pl-8'
                      : ''
                  }`}
                >
                  <div className="mx-auto flex items-center justify-center">
                    {idx === 0 && <Heart className="h-8 w-8 text-slate-900" />}
                    {idx === 1 && <Target className="h-8 w-8 text-slate-900" />}
                    {idx === 2 && <Sparkles className="h-8 w-8 text-slate-900" />}
                  </div>
                  <h3 className="text-lg font-bold text-blue-950">{value.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.text}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* CTA Banner - Full Width */}
      <ScrollReveal direction="up" duration={900} className="mt-20">
        <div className="bg-gradient-to-r from-blue-950 to-blue-900 text-white py-16 px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="mx-auto max-w-4xl space-y-6">
            <h3 className="text-2xl font-bold sm:text-3xl">Lad os tage en snak om dine mål</h3>
            <p className="text-slate-300 text-base max-w-2xl mx-auto">
              Uanset om du vil arbejde med begrænsende tanker, skabe bedre vaner eller styrke din motivation, står jeg klar til at hjælpe dig.
            </p>
            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-none bg-sky-400 px-6 py-3.5 text-sm font-bold text-blue-950 hover:bg-sky-300 hover:-translate-y-1 transition-all duration-300"
              >
                Book en samtale
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
}
